import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Brain, 
  Sparkles, 
  Volume2, 
  Settings, 
  ArrowRight,
  Check,
  ChevronRight,
  Menu,
  X,
  Smartphone,
  Monitor,
  Tablet,
  Target,
  TrendingUp,
  Globe,
  Linkedin,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import { useState } from "react";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Company logo URL
  const companyLogo = "https://customer-assets.emergentagent.com/job_focusread-3/artifacts/sf7izyfr_Master%20readbest_logo%20copy.jpg";

  const founders = [
    {
      name: "Dr. Raghavendra Gowd MC",
      role: "Chief Executive Officer",
      image: "https://customer-assets.emergentagent.com/job_focusread-3/artifacts/zg05yak9_Screenshot%202026-01-07%20at%208.54.30%E2%80%AFPM.png"
    },
    {
      name: "Jayesh Koriya",
      role: "Chief Financial Officer",
      image: "https://customer-assets.emergentagent.com/job_focusread-3/artifacts/zg05yak9_Screenshot%202026-01-07%20at%208.54.30%E2%80%AFPM.png"
    },
    {
      name: "Vikas Sharma",
      role: "Chief Marketing Officer",
      image: "https://customer-assets.emergentagent.com/job_focusread-3/artifacts/zg05yak9_Screenshot%202026-01-07%20at%208.54.30%E2%80%AFPM.png"
    },
    {
      name: "Uma Kukreja",
      role: "Head of Product Design",
      image: "https://customer-assets.emergentagent.com/job_focusread-3/artifacts/zg05yak9_Screenshot%202026-01-07%20at%208.54.30%E2%80%AFPM.png"
    }
  ];

  // Individual founder images - cropped positions for each founder
  const founderImages = {
    raghavendra: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400",
    jayesh: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400",
    vikas: "https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg?auto=compress&cs=tinysrgb&w=400",
    uma: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400"
  };

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "LD & ADHD-Specific Profiles",
      description: "Tailored reading experiences with text/file formatting designed specifically for learning disabilities and ADHD conditions."
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "AI Text Condensation & Summarization",
      description: "Intelligent text condensation and AI-powered summarization to improve content digestion and comprehension."
    },
    {
      icon: <Volume2 className="w-8 h-8" />,
      title: "Text-to-Speech",
      description: "Built-in text-to-speech with customizable reading speed and dyslexia-friendly visual guides."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Personalized Learning Paths",
      description: "Customizable fonts, colors, spacing, and multisensory approach for individualized learning experiences."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Cross-Platform Access",
      description: "Available as a dedicated website accessible on web, iOS, and Android - no multiple apps needed."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Progress Monitoring",
      description: "Continuous progress tracking with interactive and gamified content to keep learners engaged."
    }
  ];

  const problems = [
    {
      title: "Lack of LD Focus",
      description: "Current readers often only address blindness/dyslexia, missing broader learning disability needs."
    },
    {
      title: "No ADHD Features",
      description: "Existing tools lack summarization features essential for focus and comprehension in ADHD students."
    },
    {
      title: "Platform Fragmentation",
      description: "Students need multiple apps across devices, creating a fragmented and frustrating experience."
    }
  ];

  const testimonials = [
    {
      quote: "readBest has transformed how I study. The summarization feature helps me understand complex texts so much faster.",
      name: "Tracy",
      role: "Parent - Enrichment Enthusiast",
      description: "Highly involved in finding effective tools for her child with reading disorders"
    },
    {
      quote: "Finally, a tool that understands ADHD. The text condensation helps my son stay focused on what matters.",
      name: "Paul & Lauren",
      role: "Parents - Deal Diggers",
      description: "Found the free tier valuable before committing to premium features"
    },
    {
      quote: "As a student with dyslexia, I love how I can customize everything. The dyslexia-friendly fonts make reading so much easier.",
      name: "Alex",
      role: "Self-Driven Student",
      description: "High school student using readBest independently for academic success"
    }
  ];

  const pricingTiers = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: [
        "Text/file re-formatting",
        "Two profiles (LD, ADHD)",
        "Basic customization",
        "Limited fonts & styles",
        "Web access"
      ],
      cta: "Get Started Free",
      highlighted: false
    },
    {
      name: "Tier 1",
      price: "$5",
      period: "/month",
      features: [
        "Everything in Free",
        "Advanced customization",
        "Export to ePub & other formats",
        "Keyword replacement & identification",
        "Paragraph condensation",
        "Active voice conversion"
      ],
      cta: "Start Free Trial",
      highlighted: true
    },
    {
      name: "Tier 2+",
      price: "Coming Soon",
      period: "",
      features: [
        "Everything in Tier 1",
        "PDF, PPT input support",
        "Audio/video input",
        "URL input",
        "AI integrated summarizer",
        "New features as released"
      ],
      cta: "Join Waitlist",
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F7FC]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-purple-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={companyLogo} alt="readBest Logo" className="h-14 w-auto" />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-[#4A5568] hover:text-[#7C3AED] font-medium transition-colors">
                Features
              </a>
              <a href="#how-it-works" className="text-[#4A5568] hover:text-[#7C3AED] font-medium transition-colors">
                How It Works
              </a>
              <a href="#pricing" className="text-[#4A5568] hover:text-[#7C3AED] font-medium transition-colors">
                Pricing
              </a>
              <a href="#team" className="text-[#4A5568] hover:text-[#7C3AED] font-medium transition-colors">
                Team
              </a>
              <a href="#contact" className="text-[#4A5568] hover:text-[#7C3AED] font-medium transition-colors">
                Contact
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-[#4A5568] hover:text-[#7C3AED]" data-testid="nav-login-btn">
                  Log In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-6 font-semibold" data-testid="nav-signup-btn">
                  Get Started Free
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-purple-100 py-4 px-6 animate-slide-up">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-[#4A5568] hover:text-[#7C3AED] font-medium py-2">
                Features
              </a>
              <a href="#how-it-works" className="text-[#4A5568] hover:text-[#7C3AED] font-medium py-2">
                How It Works
              </a>
              <a href="#pricing" className="text-[#4A5568] hover:text-[#7C3AED] font-medium py-2">
                Pricing
              </a>
              <a href="#team" className="text-[#4A5568] hover:text-[#7C3AED] font-medium py-2">
                Team
              </a>
              <a href="#contact" className="text-[#4A5568] hover:text-[#7C3AED] font-medium py-2">
                Contact
              </a>
              <div className="flex flex-col gap-3 pt-4 border-t border-purple-100">
                <Link to="/login">
                  <Button variant="outline" className="w-full font-semibold border-[#7C3AED] text-[#7C3AED]">Log In</Button>
                </Link>
                <Link to="/register">
                  <Button className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-12 bg-gradient-to-b from-[#F8F7FC] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-[#7C3AED] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MapPin className="w-4 h-4" />
                Proudly Canadian. Globally Innovative.
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1E3A8A] leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Empowering Minds,{" "}
                <span className="text-[#7C3AED]">Embracing Differences.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#4A5568] mb-8 leading-relaxed max-w-lg">
                readBest is a digital reading tool designed specifically for students with learning disabilities and ADHD. 
                AI-powered text condensation and summarization backed by research.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-auto" data-testid="hero-cta-btn">
                    Start Reading Better
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-semibold border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-blue-50 w-full sm:w-auto">
                    See How It Works
                  </Button>
                </a>
              </div>
              {/* Platform badges */}
              <div className="flex items-center gap-4 mt-8 text-[#718096]">
                <span className="text-sm font-medium">Available on:</span>
                <div className="flex items-center gap-3">
                  <Monitor className="w-5 h-5" />
                  <Tablet className="w-5 h-5" />
                  <Smartphone className="w-5 h-5" />
                </div>
              </div>
            </div>
            <div className="relative animate-slide-in-right">
              <div className="relative z-10">
                <img 
                  src="https://images.pexels.com/photos/5212345/pexels-photo-5212345.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Students learning with readBest"
                  className="rounded-2xl shadow-2xl w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#7C3AED]/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#1E3A8A]/20 rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Problems We Solve
            </h2>
            <p className="text-blue-200 max-w-2xl mx-auto">
              Students with learning disabilities and ADHD face unique challenges that existing tools don't address.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((problem, index) => (
              <Card key={index} className="bg-white/10 border-none text-white backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-[#7C3AED] flex items-center justify-center mb-4 text-xl font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {problem.title}
                  </h3>
                  <p className="text-blue-100">{problem.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 px-6 md:px-12 bg-[#F8F7FC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Built for How You Learn
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Every feature is designed with accessibility and ease of use in mind for students with ADHD and learning disabilities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border border-purple-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-[#7C3AED] mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-[#4A5568] leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Simple Steps to Better Learning
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Get started in minutes and experience the difference.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Select Your Profile",
                description: "Choose from Dyslexia or ADHD profiles tailored to your specific learning needs."
              },
              {
                step: "02",
                title: "Paste or Upload Text",
                description: "Add any text you want to read - articles, documents, study materials, or even URLs."
              },
              {
                step: "03",
                title: "Learn Your Way",
                description: "Use AI summarization, text condensation, text-to-speech, and customized display settings."
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-7xl font-bold text-[#7C3AED]/10 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#4A5568]">{item.description}</p>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-[#7C3AED]/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-32 px-6 md:px-12 bg-[#F8F7FC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Simple, Affordable Pricing
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Start free and upgrade as you need more features.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`border-2 ${tier.highlighted ? 'border-[#7C3AED] shadow-xl scale-105' : 'border-purple-100'} bg-white`}
              >
                <CardContent className="p-8">
                  {tier.highlighted && (
                    <div className="bg-[#7C3AED] text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {tier.name}
                  </h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-[#1E3A8A]">{tier.price}</span>
                    <span className="text-[#718096]">{tier.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                        <span className="text-[#4A5568] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button 
                      className={`w-full rounded-full ${
                        tier.highlighted 
                          ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white' 
                          : 'bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-purple-50'
                      }`}
                    >
                      {tier.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Loved by Learners & Parents
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              See how readBest is helping students learn with confidence.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border border-purple-100 shadow-sm bg-white">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-[#F59E0B]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-[#4A5568] mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-[#1E3A8A]">{testimonial.name}</p>
                    <p className="text-sm text-[#7C3AED]">{testimonial.role}</p>
                    <p className="text-xs text-[#718096] mt-1">{testimonial.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section with Founders Image */}
      <section id="team" className="py-20 md:py-32 px-6 md:px-12 bg-[#F8F7FC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Meet Our Team
            </h2>
            <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
              Passionate about transforming minds with innovative software solutions. Based in Canada.
            </p>
          </div>
          
          {/* Display the founders image */}
          <div className="max-w-5xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_focusread-3/artifacts/zg05yak9_Screenshot%202026-01-07%20at%208.54.30%E2%80%AFPM.png"
              alt="readBest Founders - Dr. Raghavendra Gowd MC (CEO), Jayesh Koriya (CFO), Vikas Sharma (CMO), Uma Kukreja (Head of Product Design)"
              className="w-full rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* SDG Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-3 bg-[#E11D48]/10 text-[#E11D48] px-6 py-3 rounded-full mb-6">
            <Target className="w-6 h-6" />
            <span className="font-semibold">Supporting UN SDG 4: Quality Education</span>
          </div>
          <p className="text-[#4A5568] text-lg">
            readBest is committed to ensuring inclusive and equitable quality education and promoting lifelong learning opportunities for all.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 px-6 md:px-12 bg-[#F8F7FC]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A8A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Get In Touch
          </h2>
          <p className="text-lg text-[#4A5568] mb-12">
            Have questions? We'd love to hear from you.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border border-purple-100 bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-[#7C3AED] mx-auto mb-4">
                  <Phone className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-[#1E3A8A] mb-2">Phone</h3>
                <a href="tel:+17789511717" className="text-[#7C3AED] hover:underline">+1 (778) 951-1717</a>
              </CardContent>
            </Card>
            
            <Card className="border border-purple-100 bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-[#7C3AED] mx-auto mb-4">
                  <Mail className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-[#1E3A8A] mb-2">Email</h3>
                <a href="mailto:info@readbest.ca" className="text-[#7C3AED] hover:underline">info@readbest.ca</a>
              </CardContent>
            </Card>
            
            <Card className="border border-purple-100 bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-[#7C3AED] mx-auto mb-4">
                  <MapPin className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-[#1E3A8A] mb-2">Location</h3>
                <p className="text-[#4A5568] text-sm">Administration Office</p>
                <p className="text-[#4A5568] text-sm">6711 Baker Rd</p>
                <p className="text-[#4A5568] text-sm">Delta BC V4E 2T8</p>
                <p className="text-[#4A5568] text-sm">Canada</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-gradient-to-r from-[#7C3AED] to-[#1E3A8A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Transform Your Learning Experience?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Join thousands of students who have discovered a better way to learn. Start your free account today.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#7C3AED] hover:bg-purple-50 rounded-full px-10 py-6 text-lg font-semibold" data-testid="cta-signup-btn">
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="mt-4 text-purple-200 text-sm">No credit card required • Free tier available forever</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={companyLogo} alt="readBest Logo" className="h-12 w-auto" />
              </div>
              <p className="text-blue-200 text-sm mb-2 font-semibold">
                Proudly Canadian. Globally Innovative.
              </p>
              <p className="text-blue-200 text-sm mb-4">
                Transforming learning for students with ADHD and learning disabilities.
              </p>
              <div className="space-y-2 text-blue-200 text-sm">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+17789511717" className="hover:text-white">+1 (778) 951-1717</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href="mailto:info@readbest.ca" className="hover:text-white">info@readbest.ca</a>
                </p>
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>6711 Baker Rd, Delta BC V4E 2T8, Canada</span>
                </p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-blue-200 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-blue-200 text-sm">
                <li><a href="#team" className="hover:text-white transition-colors">Team</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-blue-200 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-blue-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-blue-200 text-sm">
              © 2024 Read Help Technologies Inc. All rights reserved. | Proudly Canadian. Globally Innovative.
            </p>
            <div className="flex gap-4">
              <a href="https://readbest.ca/" target="_blank" rel="noopener noreferrer" className="text-blue-200 hover:text-white transition-colors">
                <Globe className="w-5 h-5" />
              </a>
              <a href="#" className="text-blue-200 hover:text-white transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
